/**
 * Shared roster mutations — writes both brand and admin can perform.
 * Routes are unified /api/v1/roster/* — BE enforces role-gating per handler.
 */
import { apiClient } from '@discovr/common';
import { postThreadComment } from './shared';

const approveCreator = (rosterEntryId: string, payload?: { selected_deliverables?: string[] }): Promise<void> =>
  apiClient
    .post(`/roster/${encodeURIComponent(rosterEntryId)}/approve`, payload ?? {})
    .then(() => undefined);

/** Per-outcome counts returned by the bulk approve-all endpoint. */
export interface BulkApproveResult {
  approved_count: number;
  skipped_count: number;
  failed_count: number;
}

/**
 * Bulk "approve all eligible" creators on a campaign. The route is unified; the
 * BE dispatches by role — brand approves pending creators that have a main
 * deliverable selected (others are skipped), admin advances every
 * brand-accepted creator. Returns per-outcome counts.
 */
const approveAllCreators = (campaignId: string): Promise<BulkApproveResult> =>
  apiClient
    .post<{ data: BulkApproveResult }>(
      `/campaigns/${encodeURIComponent(campaignId)}/roster/approve-all`,
      {},
    )
    .then((res) => res.data.data);

/** Mirrors the BE cap (limits.MaxBulkRosterEntries) on one bulk request. */
export const MAX_BULK_ROSTER_SELECTION = 200;

/** Which bulk approval the caller performs — see bulkApproveCreators. */
export type BulkApproveMode = 'approve' | 'approve-as-brand';

export interface BulkApproveItem {
  entry_id: string;
  /** Main + add-ons. Required for brand approve and approve-as-brand; ignored
   *  by the admin final approve. */
  selected_deliverables?: string[];
}

/** One entry the BE could not process, with a user-facing reason. */
export interface BulkRosterFailure {
  entry_id: string;
  reason: string;
}

/** `ignored_ids` lists ids the request repeated; the first copy was processed, so they are not failures. */
export interface BulkApproveSelectedResult {
  approved_count: number;
  approved_ids: string[];
  failed: BulkRosterFailure[];
  ignored_ids: string[];
}

export interface BulkDeleteSelectedResult {
  deleted_count: number;
  deleted_ids: string[];
  failed: BulkRosterFailure[];
  ignored_ids: string[];
}

/** Wire shapes: the id lists are optional so an older backend that omits them stays safe. */
interface BulkApproveSelectedResponse {
  approved_count: number;
  approved_ids?: string[];
  failed?: BulkRosterFailure[];
  ignored_ids?: string[];
}

interface BulkDeleteSelectedResponse {
  deleted_count: number;
  deleted_ids?: string[];
  failed?: BulkRosterFailure[];
  ignored_ids?: string[];
}

export interface BulkDeletePayload {
  entryIds: string[];
  confirm: string;
}

const BULK_APPROVE_PATH: Record<BulkApproveMode, string> = {
  approve: 'approve-selected',
  'approve-as-brand': 'approve-selected-as-brand',
};

const bulkRosterUrl = (campaignId: string, action: string): string =>
  `/campaigns/${encodeURIComponent(campaignId)}/roster/${action}`;

/** The ids the BE applied. An older BE sends no list, so fall back to the requested ids it did not report as failed. */
function appliedIds(
  requested: readonly string[],
  failed: readonly BulkRosterFailure[],
  reported: string[] | undefined,
): string[] {
  if (reported) return reported;
  const failedIds = new Set(failed.map((f) => f.entry_id));
  return [...new Set(requested)].filter((id) => !failedIds.has(id));
}

// `failed` is guarded so a contract drift can't throw inside the success path and report an applied batch as failed.
function toApproveResult(res: BulkApproveSelectedResponse, requested: readonly string[]): BulkApproveSelectedResult {
  const failed = res.failed ?? [];
  return {
    approved_count: res.approved_count,
    approved_ids: appliedIds(requested, failed, res.approved_ids),
    failed,
    ignored_ids: res.ignored_ids ?? [],
  };
}

function toDeleteResult(res: BulkDeleteSelectedResponse, requested: readonly string[]): BulkDeleteSelectedResult {
  const failed = res.failed ?? [];
  return {
    deleted_count: res.deleted_count,
    deleted_ids: appliedIds(requested, failed, res.deleted_ids),
    failed,
    ignored_ids: res.ignored_ids ?? [],
  };
}

/**
 * Approve only the given roster entries. `approve` is role-dispatched by the
 * BE (brand → pending with deliverables; admin → final approve of
 * brand-accepted); `approve-as-brand` is the admin proxy for pending entries.
 * Each entry is processed independently; failures come back per entry.
 */
const bulkApproveCreators = (
  campaignId: string,
  mode: BulkApproveMode,
  items: BulkApproveItem[],
): Promise<BulkApproveSelectedResult> =>
  apiClient
    .post<{ data: BulkApproveSelectedResponse }>(bulkRosterUrl(campaignId, BULK_APPROVE_PATH[mode]), { items })
    .then((res) => toApproveResult(res.data.data, items.map((item) => item.entry_id)));

/**
 * Admin-only: hard-delete the given entries (fixed-word confirm). Each one runs
 * through the same server-side gates and cascade as the single-row delete.
 */
const bulkDeleteCreators = (campaignId: string, payload: BulkDeletePayload): Promise<BulkDeleteSelectedResult> =>
  apiClient
    .post<{ data: BulkDeleteSelectedResponse }>(bulkRosterUrl(campaignId, 'delete-selected'), {
      entry_ids: payload.entryIds,
      confirm: payload.confirm,
    })
    .then((res) => toDeleteResult(res.data.data, payload.entryIds));

const rejectCreator = (rosterEntryId: string, reason: string): Promise<void> =>
  apiClient
    .post(`/roster/${encodeURIComponent(rosterEntryId)}/reject`, { reason })
    .then(() => undefined);

const selectDeliverable = (rosterEntryId: string, types: string[]): Promise<void> =>
  apiClient
    .put(`/roster/${encodeURIComponent(rosterEntryId)}/deliverables`, {
      selected_deliverables: types,
    })
    .then(() => undefined);

/**
 * PATCH /roster/:id/products — full-replace a confirmed creator's assigned
 * products (Multi-Product Campaigns). Admin and brand writers. Empty ids
 * unassigns all. The response's assigned_products is ignored; the roster refetch
 * carries the resolved products.
 */
const assignProducts = (rosterEntryId: string, productIds: string[]): Promise<void> =>
  apiClient
    .patch(`/roster/${encodeURIComponent(rosterEntryId)}/products`, {
      product_ids: productIds,
    })
    .then(() => undefined);

/** POST /roster/:id/products/keep-previous — restores the products a rollback set aside (204). */
const keepPreviousProducts = (rosterEntryId: string): Promise<void> =>
  apiClient.post(`/roster/${encodeURIComponent(rosterEntryId)}/products/keep-previous`).then(() => undefined);

const postScriptThreadComment = (rosterEntryId: string, body: string): Promise<void> =>
  postThreadComment(rosterEntryId, 'script', body).then(() => undefined);

const postContentThreadComment = (rosterEntryId: string, body: string): Promise<void> =>
  postThreadComment(rosterEntryId, 'content', body).then(() => undefined);

export function createRosterMutationsApi() {
  return {
    approveCreator,
    approveAllCreators,
    bulkApproveCreators,
    bulkDeleteCreators,
    rejectCreator,
    selectDeliverable,
    assignProducts,
    keepPreviousProducts,
    postScriptThreadComment,
    postContentThreadComment,
  };
}

export type RosterMutationsApi = ReturnType<typeof createRosterMutationsApi>;
